import SolutionLayout from "@/pages/solutions/components/SolutionLayout";

const features = [
  {
    title: "Dialer-optimized routes",
    desc: "Routes tuned for predictive and power dialers: low PDD, high ASR, and CLI presentation matched to dialer timezone to maximize answer rates.",
    icon: "ri-phone-line",
  },
  {
    title: "ASR/PDD monitoring per campaign",
    desc: "Track route performance per campaign ID. If a route degrades during a campaign, swap it without restarting the dialer session.",
    icon: "ri-line-chart-line",
  },
  {
    title: "Outbound CLI control",
    desc: "Present local CLIs matched to destination country. Supports CLI rotation, randomization, and forced geographic matching.",
    icon: "ri-fingerprint-line",
  },
  {
    title: "CPaaS REST API",
    desc: "Programmable voice sessions via a clean REST API. Initiate calls, monitor status, and retrieve recordings with webhook callbacks.",
    icon: "ri-code-s-slash-line",
  },
  {
    title: "Concurrent call scaling",
    desc: "Burst from hundreds to tens of thousands of concurrent calls without pre-provisioning. Elastic capacity follows your traffic curve.",
    icon: "ri-flashlight-line",
  },
  {
    title: "Compliance tooling",
    desc: "Timezone-based dial-window enforcement, DNC list scrubbing hooks, and recording consent flow templates per jurisdiction.",
    icon: "ri-shield-check-line",
  },
];

const stats = [
  { value: "<38ms", label: "Median PDD on dialer routes" },
  { value: "60%+", label: "Target ASR on mobile routes" },
  { value: "∞", label: "Concurrent call scaling" },
  { value: "100%", label: "CLI presentation support" },
];

export default function CallCentersCpaas() {
  return (
    <SolutionLayout
      title="Call Centers & CPaaS"
      tagline="Routes purpose-built for outbound dialers, contact centers, and programmable voice platforms. ASR optimized, CLI-matched, and dialer-ready."
      breadcrumbLabel="Call Centers & CPaaS"
    >
      <section className="pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
                <div className="text-2xl md:text-3xl font-semibold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Call center solutions</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              Routes that your dialer actually likes.
            </h2>
            <p className="mt-3 text-foreground-700 max-w-2xl">
              Predictive and power dialers are unforgiving. Low ASR kills conversion. High PDD kills efficiency. We optimize both — and give you per-campaign visibility so you can act fast.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-background-200 bg-background-50 p-6 hover:border-primary-400 transition-colors">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${f.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950 font-heading">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SolutionLayout>
  );
}